import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { observer } from 'mobx-react'
import dayjs from 'dayjs'
import _ from 'lodash'
import { Cog6ToothIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { ArrowPathIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'

// Components
import { Button } from '../../components/Button'
import { CheckBox } from '../../components/CheckBox'
import { DataTable } from '../../components/DataTable'
import { Modal } from '../../components/Modal'
import { Select } from '../../components/Select'
import { TextInput } from '../../components/TextInput'
import { Toggle } from '../../components/Toggle'
import { Tooltip } from '../../components/Tooltip'

// Icons
import Key from '../../assets/images/key.svg'

// Stores
import { NavigationStoreContext } from '../../stores/NavigationStore'

// Services
import { getEvent, updateEvent } from '../../services/events.service'

// Utils
import { CVENT_STATUSES, STOVA_STATUSES } from '../../utils/constants'
import { toast } from '../../utils/helpers'
import { hasCompletedRegistrationConfig } from './helpers'

const PRIMARY_DEFAULT = {
  registrationType: null,
  apiKey: null,
  externalAccountId: null,
  externalEventId: null,
  clientId: null,
  clientSecret: null,
  useConfirmationAsId: false,
}

const SECONDARY_DEFAULT = {
  secondaryRegistrationType: null,
  secondaryApiKey: null,
  secondaryExternalAccountId: null,
  secondaryEventName: null,
}

const SyncConfiguration = observer(({ registrationOptions, secondary = false }) => {
  // Context
  const { event, eventId, organizationId, setEvent } = useContext(NavigationStoreContext)

  // State
  const [loadingRegistration, setLoadingRegistration] = useState(false)
  const [loadingReset, setLoadingReset] = useState(false)
  const [loadingDisablePhotoSync, setLoadingDisablePhotoSync] = useState(false)
  const [loadingSync, setLoadingSync] = useState(false)
  const [pingEvent, setPingEvent] = useState(false)
  const [checkingStatus, setCheckingStatus] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [showConfirmDisablePhotoSync, setShowConfirmDisablePhotoSync] = useState(false)

  // Custom Field Sync
  const [syncInProgress, setSyncInProgress] = useState(false)
  const [loadingCustomFields, setLoadingCustomFields] = useState(false)
  const [loadingSaveOptions, setLoadingSaveOptions] = useState(false)
  const [customFields, setCustomFields] = useState([])
  const [selectedFields, setSelectedFields] = useState([])

  // Category Sync
  const [availableCategories, setAvailableCategories] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])

  // Status Sync
  const [availableStatuses, setAvailableStatuses] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([])

  const syncEnabled =
    event?.enableRegistrationSync || (secondary && event?.enableSecondaryRegistrationSync)

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccesses = (m) => toast(m, 'success')

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    reset,
    watch,
  } = useForm({
    defaultValues: secondary ? SECONDARY_DEFAULT : PRIMARY_DEFAULT,
  })

  const CONFIG_COMPLETED = useMemo(
    () => hasCompletedRegistrationConfig(event, registrationOptions, secondary),
    [event, registrationOptions, secondary],
  )

  /**
   * Triggers on first load to see if we need to monitor the registration status for this event.
   */
  useEffect(() => {
    if (syncEnabled && CONFIG_COMPLETED && !secondary && !event?.availableCustomFields) {
      setPingEvent(true)
    }

    // Primary sync
    if (!secondary) {
      reset({
        registrationType: _.find(registrationOptions, (o) => o.label === event?.registrationType),
        apiKey: event?.apiKey,
        clientId: event?.clientId,
        clientSecret: event?.clientSecret,
        externalAccountId: event?.externalAccountId,
        externalEventId: event?.externalEventId,
        useConfirmationAsId: event?.useConfirmationAsId || false,
      })
    }
    // Secondary sync
    else {
      reset({
        secondaryRegistrationType: _.find(registrationOptions, (o) => o.label === 'RCS'),
        secondaryApiKey: event?.secondaryApiKey,
        secondaryEventName: event?.secondaryEventName,
      })
    }
  }, [registrationOptions])

  useEffect(() => {
    if (!secondary) {
      if (event?.availableCustomFields) {
        const fields = _.map(_.keys(event.availableCustomFields), (k) => ({
          name: event.availableCustomFields[k].text,
          key: k,
          ...event.availableCustomFields[k],
        }))

        setCustomFields(fields)
        setSelectedFields(event.enabledCustomFields || [])
      }

      // Backwards compatibility - check if `attendeeCategories` is in the old format.
      // old format is <category-name>: { parent-id: <parent-id>, category_id: <category-id> }
      const categoryIds = _.keys(event.attendeeCategories)
      if (
        event?.attendeeCategories &&
        categoryIds.length > 0 &&
        !_.isObject(event?.attendeeCategories[categoryIds[0]])
      ) {
        const categories = _.map(_.keys(event?.attendeeCategories), (key) => ({
          id: key,
          name: event.attendeeCategories[key],
        }))
        setAvailableCategories(categories)
      }

      if (event?.registrationType === 'Stova') {
        setAvailableStatuses(STOVA_STATUSES)
      } else if (event?.registrationType === 'Cvent') {
        setAvailableStatuses(CVENT_STATUSES)
      }

      setSelectedCategories(event?.enabledAttendeeCategories || [])
      setSelectedStatuses(event?.enabledAttendeeStatuses || [])
    }
  }, [])

  /**
   * Pinging event is only for primary syncs.
   */
  useEffect(() => {
    let interval = null

    if (pingEvent) {
      setCheckingStatus(true)

      if (interval === null) {
        interval = setInterval(async () => {
          const updatedEvent = await getEvent(organizationId, eventId)
          setEvent(updatedEvent)

          if (
            updatedEvent.availableCustomFields ||
            updatedEvent.attendeesSyncStatusMessage ||
            updatedEvent.customFieldsSyncStatusMessage
          ) {
            clearInterval(interval)
            setCheckingStatus(false)
            setPingEvent(false)
          }
        }, 3000)
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [pingEvent])

  /**
   * Handles submitting the registration sync form to initiate the sync process.
   * @param {object} data
   */
  const onRegistrationSubmit = async (data) => {
    const updatedData = { ...data }

    // Primary sync
    if (!secondary) {
      updatedData.registrationType = updatedData.registrationType?.id
      updatedData.triggerAttendeeSyncAllTime = true

      // If the api key includes `*`, then it is already set and
      // we don't want to update it.
      if (updatedData.apiKey?.includes('*')) {
        delete updatedData.apiKey
      }
      if (updatedData.clientId?.includes('*')) {
        delete updatedData.clientId
      }
      if (updatedData.clientSecret?.includes('*')) {
        delete updatedData.clientSecret
      }
    }
    // Secondary sync
    else {
      updatedData.secondaryRegistrationType = 'RCS'
      updatedData.triggerSecondaryAttendeeSyncAllTime = true

      // If the api key includes `*`, then it is already set and
      // we don't want to update it.
      if (updatedData.secondaryApiKey?.includes('*')) {
        delete updatedData.secondaryApiKey
      }
    }

    const result = await updateEvent(
      { id: eventId, ...updatedData },
      handleErrors,
      setLoadingRegistration,
      () => {},
    )

    if (result) {
      handleSuccesses(`${secondary ? 'Secondary' : 'Registration'} sync initiated.`)
      if (!secondary) setPingEvent(true)
    }
  }

  /**
   * Handles submitting the merge fields form to update the selected custom fields.
   * - Only for primary syncs
   */
  const onMergeFieldsSubmit = async () => {
    const result = await updateEvent(
      {
        id: eventId,
        enabledAttendeeCategories: selectedCategories,
        enabledAttendeeStatuses: selectedStatuses,
        enabledCustomFields: selectedFields,
        triggerCustomFieldsSync: true,
      },
      handleErrors,
      setLoadingCustomFields,
      () => {},
    )

    if (result) {
      handleSuccesses('Merge field sync initiated.')
      setEvent(result)
      setSyncInProgress(true)
    }
  }

  /**
   * Handles saving the option settings (selected categories, statuses, and custom fields)
   * without triggering a resync.
   */
  const onSaveOptionSettings = async () => {
    const result = await updateEvent(
      {
        id: eventId,
        enabledAttendeeCategories: selectedCategories,
        enabledAttendeeStatuses: selectedStatuses,
        enabledCustomFields: selectedFields,
      },
      handleErrors,
      setLoadingSaveOptions,
      () => {},
    )

    if (result) {
      handleSuccesses('Option settings saved.')
      setEvent(result)
    }
  }

  /**
   * Handles submitting a reset registration request for the event.
   */
  const onResetSubmit = async () => {
    const data = { id: eventId }

    if (secondary) {
      data.triggerResetSecondarySync = true
    } else {
      data.triggerResetRegistrationSync = true
    }

    const result = await updateEvent(data, handleErrors, setLoadingReset, () => {})

    if (result) {
      handleSuccesses(`${secondary ? 'Secondary' : 'Registration'} sync reset.`)
      setEvent(result)

      // Clear out all monitoring
      reset(secondary ? SECONDARY_DEFAULT : PRIMARY_DEFAULT)
      setShowResetConfirm(false)
      setPingEvent(false)
      setCheckingStatus(false)
    }
  }

  /**
   * Display the respective status and last updated at based on the sync type.
   */
  const renderStatusMessage = () => {
    if (secondary) {
      return (
        <>
          {event?.secondaryAttendeesLastSyncedAt && !event.secondaryAttendeesSyncStatusMessage && (
            <span className="text-xs">
              Last Updated{' '}
              {dayjs(event.secondaryAttendeesLastSyncedAt).format('MM/DD/YYYY h:mm A')}
            </span>
          )}

          {event?.secondaryAttendeesSyncStatusMessage && (
            <span className="text-end text-xs text-error">
              {event.secondaryAttendeesSyncStatusMessage}
            </span>
          )}
        </>
      )
    }

    return (
      <>
        {event?.attendeesLastSyncedAt && !event.attendeesSyncStatusMessage && (
          <span className="text-xs">
            Last Updated {dayjs(event.attendeesLastSyncedAt).format('MM/DD/YYYY h:mm A')}
          </span>
        )}

        {event?.attendeesSyncStatusMessage && (
          <span className="text-end text-xs text-error">{event.attendeesSyncStatusMessage}</span>
        )}
      </>
    )
  }

  const renderRegistrationConfigFields = () => {
    const fields = []

    const selected = watch(secondary ? 'secondaryRegistrationType' : 'registrationType')
    if (!selected) return null

    const registrationOption = _.find(registrationOptions, (o) => o.id === selected.id)
    if (registrationOption?.type === 'API Key') {
      // Only primary syncs have an external account ID (RCS does not use this)
      if (!secondary) {
        fields.push(
          <TextInput
            className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
            disabled={loadingRegistration || checkingStatus || CONFIG_COMPLETED}
            icon={<Cog6ToothIcon className="ml-1.5 h-5 stroke-purple" />}
            data-testid="externalAccountId"
            error={
              errors[secondary ? 'secondaryExternalAccountId' : 'externalAccountId'] &&
              'This field is required'
            }
            fullWidth
            id={secondary ? 'secondaryExternalAccountId' : 'externalAccountId'}
            inputStyles="rounded-none font-nunito text-md sm:text-base"
            name={secondary ? 'secondaryExternalAccountId' : 'externalAccountId'}
            nunito
            placeholder="Account ID"
            {...register(secondary ? 'secondaryExternalAccountId' : 'externalAccountId', {
              required: true,
            })}
          />,
        )
      }
      fields.push(
        <TextInput
          className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
          disabled={loadingRegistration || checkingStatus || CONFIG_COMPLETED}
          icon={<img alt="Key" className="ml-1.5 h-4" src={Key} />}
          data-testid="api-key"
          error={errors[secondary ? 'secondaryApiKey' : 'apiKey'] && 'This field is required'}
          fullWidth
          id={secondary ? 'secondaryApiKey' : 'apiKey'}
          inputStyles="rounded-none font-nunito text-md sm:text-base"
          name={secondary ? 'secondaryApiKey' : 'apiKey'}
          nunito
          placeholder="API Key"
          {...register(secondary ? 'secondaryApiKey' : 'apiKey', { required: true })}
        />,
      )
    } else if (registrationOption?.type === 'OAuth 2.0 Client Credentials') {
      fields.push(
        <TextInput
          className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
          disabled={loadingRegistration || checkingStatus || CONFIG_COMPLETED}
          icon={<img alt="Key" className="ml-1.5 h-4" src={Key} />}
          data-testid="client-id"
          error={errors.clientId && 'This field is required'}
          fullWidth
          id="clientId"
          inputStyles="rounded-none font-nunito text-md sm:text-base"
          name="clientId"
          nunito
          placeholder="Client ID"
          {...register('clientId', { required: true })}
        />,
      )
      fields.push(
        <TextInput
          className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
          disabled={loadingRegistration || checkingStatus || CONFIG_COMPLETED}
          icon={<img alt="Key" className="ml-1.5 h-4" src={Key} />}
          data-testid="client-secret"
          error={errors.clientSecret && 'This field is required'}
          fullWidth
          id="clientSecret"
          inputStyles="rounded-none font-nunito text-md sm:text-base"
          name="clientSecret"
          nunito
          placeholder="Client Secret"
          {...register('clientSecret', { required: true })}
        />,
      )
    }

    return <div className="flex w-full flex-col gap-2 sm:flex-row">{fields}</div>
  }

  const syncError =
    event && (event.customFieldsSyncStatusMessage || event.attendeesSyncStatusMessage)

  return (
    <>
      <div className="mb-4 flex flex-col">
        <div className="mb-2 flex flex-col items-start justify-between gap-1 sm:flex-row">
          <span className="text-md font-bold">Registration Details</span>

          <div className="flex flex-col items-center gap-2 sm:flex-row">
            <div className="flex shrink-0 flex-col gap-1">{renderStatusMessage()}</div>

            {CONFIG_COMPLETED && (
              <Button
                className="border-red bg-white text-red hover:bg-white"
                fullWidth
                onClick={() => setShowResetConfirm(true)}
                outlined
                label="Reset API Connection"
              />
            )}
          </div>
        </div>

        <div className="flex w-full flex-col space-y-2">
          <div className="flex w-full flex-col gap-2 sm:flex-row">
            <Controller
              name={secondary ? 'secondaryRegistrationType' : 'registrationType'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  data-testid="registrationType"
                  disabled={loadingRegistration || checkingStatus || CONFIG_COMPLETED}
                  error={
                    errors[secondary ? 'secondaryRegistrationType' : 'registrationType'] &&
                    'This field is required'
                  }
                  id={secondary ? 'secondaryRegistrationType' : 'registrationType'}
                  name={secondary ? 'secondaryRegistrationType' : 'registrationType'}
                  nunito
                  onChange={onChange}
                  options={registrationOptions}
                  placeholder="Select a Registration System"
                  style={{ flex: true, width: '100%' }}
                  value={value}
                />
              )}
              rules={{ required: true }}
            />

            <TextInput
              className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
              disabled={loadingRegistration || checkingStatus || CONFIG_COMPLETED}
              icon={<Cog6ToothIcon className="ml-1.5 h-5 stroke-purple" />}
              data-testid="externalEventId"
              error={
                errors[secondary ? 'secondaryEventName' : 'externalEventId'] &&
                'This field is required'
              }
              fullWidth
              id={secondary ? 'secondaryEventName' : 'externalEventId'}
              inputStyles="rounded-none font-nunito text-md sm:text-base"
              name={secondary ? 'secondaryEventName' : 'externalEventId'}
              nunito
              placeholder="Event ID"
              {...register(secondary ? 'secondaryEventName' : 'externalEventId', {
                required: true,
              })}
            />
          </div>

          {renderRegistrationConfigFields()}

          {!secondary && (
            <Controller
              name="useConfirmationAsId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div>
                  <p className="font-nunito font-semibold">Attendee Stored By</p>
                  <p className="text-xs text-gray-600">
                    Configure how the attendee data will be accessed from firestore. This will be
                    put into the Attendee QR code when added to a badge. This will be locked once
                    configuration is connected, unless reset.
                  </p>
                  <Toggle
                    className="mt-1"
                    disabled={loadingRegistration || checkingStatus || CONFIG_COMPLETED}
                    label={
                      <span className="text-sm">
                        Use Confirmation Code instead of Internal EventStack ID
                      </span>
                    }
                    name="useConfirmationAsId"
                    onChange={onChange}
                    checked={value}
                  />
                </div>
              )}
            />
          )}
        </div>

        {!CONFIG_COMPLETED && (
          <Button
            background="bg-purple border-purple hover:bg-purple-600"
            className="mt-4 self-end"
            loading={loadingRegistration}
            label="Connect to Registration API"
            onClick={handleSubmit(onRegistrationSubmit)}
          />
        )}
      </div>

      {pingEvent && !syncError && (
        <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
          <span className="text-2xl font-bold">Syncing merge fields...</span>
          <span className="flex items-center pr-3">
            <div className="h-10 w-10">
              {/* eslint-disable-next-line tailwindcss/no-custom-classname, tailwindcss/classnames-order */}
              <svg className="h-10 w-10 motion-safe:animate-spin-slow" viewBox="0 0 40 40">
                <ArrowPathIcon className="h-10 w-10" aria-hidden="true" />
              </svg>
            </div>
          </span>
        </div>
      )}

      {CONFIG_COMPLETED && !secondary && event?.availableCustomFields && (
        <div className="mt-4 flex h-full max-h-[400px] grow flex-col space-y-2 overflow-hidden">
          <div className="flex flex-col gap-1">
            <span className="text-md font-bold">API Settings</span>

            <div className="flex flex-col justify-between gap-2 sm:flex-row">
              <Toggle
                className="mb-3"
                checked={event.enablePhotoSync}
                id="enablePhotoSync"
                label="Sync Photos from API"
                labelClassName="sm:flex-none"
                onChange={async () => {
                  if (event.enablePhotoSync) {
                    setShowConfirmDisablePhotoSync(true)
                  } else {
                    const result = await updateEvent(
                      { id: eventId, enablePhotoSync: true },
                      handleErrors,
                      () => {},
                      () => {},
                    )

                    if (result) {
                      handleSuccesses('Photo sync enabled.')
                      setEvent(result)
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="flex flex-row items-center justify-between pb-4">
            <span className="text-md font-bold">Available Options</span>

            <div className="flex flex-col items-end space-y-1">
              <div className="flex flex-row gap-2">
                <Button
                  background="bg-purple border-purple hover:bg-purple-600"
                  disabled={syncInProgress || loadingCustomFields}
                  label="Save Option Settings"
                  loading={loadingSaveOptions}
                  onClick={onSaveOptionSettings}
                />

                <Tooltip
                  content={
                    <div className="rounded-lg bg-white px-2 pb-0.5">
                      <span className="text-xs">Sync is in progress.</span>
                    </div>
                  }
                  display={syncInProgress}
                  placement="left"
                >
                  <Button
                    background="bg-purple border-purple hover:bg-purple-600"
                    disabled={syncInProgress}
                    label="Resync Configuration Options"
                    loading={loadingCustomFields}
                    onClick={onMergeFieldsSubmit}
                  />
                </Tooltip>
              </div>

              {event.customFieldsLastSyncedAt && !event.customFieldsSyncStatusMessage && (
                <span className="text-xs">
                  Last Updated {dayjs(event.customFieldsLastSyncedAt).format('MM/DD/YYYY h:mm A')}
                </span>
              )}

              {event.customFieldsSyncStatusMessage && (
                <span className="text-xs text-error">{event.customFieldsSyncStatusMessage}</span>
              )}
            </div>
          </div>

          <span className="text-sm font-bold">Available Merge Fields</span>
          <span className="text-xs text-gray-600">
            Select the merge fields you want to use in badge logic, popups, and other features.
            Only enabled fields will appear as options.
          </span>

          <DataTable
            columns={[
              {
                id: 'select',
                width: '135px',
                name: (
                  <div className="flex flex-row">
                    <CheckBox
                      className="ml-2"
                      onChange={() => {
                        if (
                          selectedFields.length === 0 ||
                          selectedFields.length < customFields.length
                        ) {
                          setSelectedFields(_.map(customFields, 'key'))
                        } else {
                          setSelectedFields([])
                        }
                      }}
                      value={selectedFields.length === customFields.length}
                    />
                    <span className="text-xs uppercase">Select All</span>
                  </div>
                ),
                cell: (row) => (
                  <div className="">
                    <CheckBox
                      className="ml-2"
                      onChange={() => {
                        if (selectedFields.includes(row.name)) {
                          setSelectedFields(selectedFields.filter((f) => f !== row.key))
                        } else {
                          setSelectedFields([...selectedFields, row.key])
                        }
                      }}
                      value={selectedFields.includes(row.key)}
                    />
                  </div>
                ),
                sortable: false,
              },
              {
                id: 'field',
                grow: 1,
                name: 'Field Name',
                selector: (row) => row.name,
                sortable: true,
                width: '300px',
              },
              {
                id: 'type',
                name: 'Type',
                selector: (row) => row.type || '—',
                sortable: true,
                width: '150px',
              },
              {
                id: 'options',
                name: 'Options',
                grow: 1,
                cell: (row) =>
                  row.options && row.options.length > 0 ? (
                    <span className="truncate" title={row.options.join(', ')}>
                      {row.options.join(', ')}
                    </span>
                  ) : (
                    '—'
                  ),
                sortable: false,
              },
              {
                id: 'visibility',
                grow: 1,
                name: 'Visible',
                selector: (row) => (row.questionId ? 'Yes' : 'No'),
                sortable: true,
                omit: event.registrationType !== 'Stova',
              },
            ]}
            data={customFields}
            defaultSortFieldId="name"
            defaultSortAsc
            progressPending={loadingCustomFields}
            responsive
          />
        </div>
      )}

      {CONFIG_COMPLETED && secondary && (
        <Button
          background="bg-purple border-purple hover:bg-purple-600"
          className="w-auto self-end"
          fullWidth
          label="Trigger Manual Sync"
          loading={loadingSync}
          onClick={async () => {
            const result = await updateEvent(
              { id: eventId, triggerSecondaryAttendeeSyncAllTime: true },
              handleErrors,
              setLoadingSync,
              () => {},
            )

            if (result) {
              handleSuccesses('Secondary sync initiated.')
            }
          }}
        />
      )}

      {CONFIG_COMPLETED && !secondary && availableCategories.length > 0 && (
        <div className="mt-4 flex h-full max-h-[400px] grow flex-col space-y-2 overflow-hidden ">
          <div className="flex flex-col items-start justify-between space-y-1 sm:flex-row sm:space-y-0">
            <span className="text-sm font-bold">Available Categories</span>
          </div>
          <span className="text-xs text-gray-600">
            Filter which categories are considered valid during sync. If none are selected, all
            categories will be included.
          </span>

          <DataTable
            columns={[
              {
                id: 'select',
                width: '135px',
                name: (
                  <div className="flex flex-row">
                    <CheckBox
                      className="ml-2"
                      onChange={() => {
                        if (
                          selectedCategories.length === 0 ||
                          selectedCategories.length < availableCategories.length
                        ) {
                          setSelectedCategories(_.map(availableCategories, 'id'))
                        } else {
                          setSelectedCategories([])
                        }
                      }}
                      value={selectedCategories.length === availableCategories.length}
                    />
                    <span className="text-xs uppercase">Select All</span>
                  </div>
                ),
                cell: (row) => (
                  <div className="">
                    <CheckBox
                      className="ml-2"
                      onChange={() => {
                        if (selectedCategories.includes(row.id)) {
                          setSelectedCategories(selectedCategories.filter((f) => f !== row.id))
                        } else {
                          setSelectedCategories([...selectedCategories, row.id])
                        }
                      }}
                      value={selectedCategories.includes(row.id)}
                    />
                  </div>
                ),
                sortable: false,
              },
              {
                id: 'category',
                grow: 1,
                name: 'Category Name',
                selector: (row) => row.name,
                sortable: true,
              },
            ]}
            data={availableCategories}
            defaultSortFieldId="name"
            defaultSortAsc
            progressPending={loadingCustomFields}
            responsive
          />
        </div>
      )}

      {!pingEvent && CONFIG_COMPLETED && !secondary && availableStatuses.length > 0 && (
        <div className="mt-4 flex h-full max-h-[400px] grow flex-col space-y-2 overflow-hidden ">
          <div className="flex flex-col items-start justify-between space-y-1 sm:flex-row sm:space-y-0">
            <span className="text-sm font-bold">Available Statuses</span>
          </div>

          <DataTable
            columns={[
              {
                id: 'select',
                width: '135px',
                name: (
                  <div className="flex flex-row">
                    <CheckBox
                      className="ml-2"
                      onChange={() => {
                        if (
                          selectedStatuses.length === 0 ||
                          selectedStatuses.length < availableStatuses.length
                        ) {
                          setSelectedStatuses(_.map(availableStatuses, 'id'))
                        } else {
                          setSelectedStatuses([])
                        }
                      }}
                      value={selectedStatuses.length === availableStatuses.length}
                    />
                    <span className="text-xs uppercase">Select All</span>
                  </div>
                ),
                cell: (row) => (
                  <div className="">
                    <CheckBox
                      className="ml-2"
                      onChange={() => {
                        if (selectedStatuses.includes(row.id)) {
                          setSelectedStatuses(selectedStatuses.filter((f) => f !== row.id))
                        } else {
                          setSelectedStatuses([...selectedStatuses, row.id])
                        }
                      }}
                      value={selectedStatuses.includes(row.id)}
                    />
                  </div>
                ),
                sortable: false,
              },
              {
                id: 'status',
                grow: 1,
                name: 'Status Name',
                selector: (row) => row.name,
                sortable: true,
              },
            ]}
            data={availableStatuses}
            defaultSortFieldId="name"
            defaultSortAsc
            progressPending={loadingCustomFields}
            responsive
          />
        </div>
      )}

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
          },
          {
            type: 'submit',
            label: 'Confirm',
            background: 'bg-red',
            onClick: () => onResetSubmit(),
          },
        ]}
        icon={<ExclamationTriangleIcon className="h-8 text-white" />}
        iconBackground="bg-red"
        content={
          <div className="mt-3 flex flex-col space-y-2 text-center sm:mt-5">
            <span className="text-sm">
              If you reset the API connection, all currently synced data will be deleted, including
              Merge Fields and Attendees.
            </span>

            <span className="text-sm">Do you want to continue and reset the API connection?</span>
          </div>
        }
        loading={loadingReset}
        open={showResetConfirm}
        setOpen={setShowResetConfirm}
        title="Are you sure?"
      />

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
          },
          {
            type: 'submit',
            label: 'Confirm',
            background: 'bg-red',
            onClick: async () => {
              const result = await updateEvent(
                { id: eventId, enablePhotoSync: false },
                handleErrors,
                setLoadingDisablePhotoSync,
                () => {},
              )

              if (result) {
                handleSuccesses('Photo sync disabled.')
                setEvent(result)
                setShowConfirmDisablePhotoSync(false)
              }
            },
          },
        ]}
        icon={<ExclamationTriangleIcon className="h-8 text-white" />}
        iconBackground="bg-red"
        content={
          <div className="mt-3 flex text-center sm:mt-5">
            <span className="text-sm">
              If photo verification is enabled for Check-in or Sessions, turning off the photo sync
              will disable those features.
            </span>
          </div>
        }
        loading={loadingDisablePhotoSync}
        open={showConfirmDisablePhotoSync}
        setOpen={setShowConfirmDisablePhotoSync}
        title="Are you sure?"
      />
    </>
  )
})

SyncConfiguration.propTypes = {
  registrationOptions: PropTypes.array.isRequired,
  secondary: PropTypes.bool,
}

export default SyncConfiguration
